import { Component, ChangeDetectionStrategy, signal, inject, computed, effect, untracked, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray, FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { ResultStatusPipe } from '../../pipes/result-status.pipe';
import { DisciplinePipe } from '../../pipes/discipline.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { BreedNamePipe } from '../../pipes/breed-name.pipe';
import { AuthService } from '../../services/auth.service';
import { HumanService } from '../../services/human.service';
import { DogService } from '../../services/dog.service';
import { Human, Dog } from '../../models/data.models';
import { RouterModule, Router } from '@angular/router';
import { TranslationService } from '../../services/translation.service';
import { normalizePersonIdentity } from '../../utils/person-identity';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { HumanAvatarComponent } from '../../components/human-avatar/human-avatar.component';
import { HumanProfileStatsComponent } from '../../components/human-profile-stats/human-profile-stats.component';
import { HumanProfileDataService } from '../../services/human-profile-data.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { HumanProfileResult } from '../../models/human-profile.models';
import { StyledSelectComponent, StyledSelectOption } from '../../components/styled-select/styled-select.component';
import { ClubPickerComponent, ClubSelection } from '../../components/club-picker/club-picker.component';
import { normalizeResultDiscipline } from '../../models/discipline.models';
import { currentSeasonStartYear, defaultSeason, seasonLabel, seasonStartYear } from '../../utils/season';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { includesEveryNameToken, nameSearchTokens } from '../../models/name-search';
import { NewPasswordFormComponent } from '../../components/change-password/new-password-form.component';

interface DogUsageStats {
  races: number;
  distance: number;
  disciplines: { name: string; races: number; distance: number }[];
}

@Component({
  selector: 'app-my-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslatePipe, DisplayValuePipe, ResultStatusPipe, DisciplinePipe, DistancePipe, BreedNamePipe, RouterModule, BrandedLoaderComponent, HumanAvatarComponent, HumanProfileStatsComponent, StyledSelectComponent, ClubPickerComponent, PageHeaderComponent, NewPasswordFormComponent],
  templateUrl: './my-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProfileComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private humanService = inject(HumanService);
  private dogService = inject(DogService);
  private profileDataService = inject(HumanProfileDataService);
  private imageUpload = inject(ImageUploadService);
  private router = inject(Router);
  private translationService = inject(TranslationService);

  // signals
  isLoading = signal(true);
  isEditMode = signal(false);
  profileSaveError = signal(false);
  showPasswordChange = signal(false);
  passwordChanged = signal(false);
  readonly currentYear = new Date().getFullYear();
  human = signal<Human | null>(null);
  myDogs = signal<Dog[]>([]);
  borrowedDogIds = signal<string[]>([]);
  savingDogRelationship = signal<string | null>(null);
  dogRelationshipError = signal(false);
  personalDogs = computed(() => this.myDogs().filter(dog => !this.borrowedDogIds().includes(dog.id)));
  racingDogs = computed(() => {
    const dogs = new Map<string, Dog>(this.myDogs().map(dog => [dog.id, dog]));
    for (const result of this.humanResults()) {
      if (result.team?.dog && !dogs.has(result.team.dog.id)) dogs.set(result.team.dog.id, result.team.dog);
    }
    return [...dogs.values()].filter(dog => (this.dogUsageStats().get(dog.id)?.races ?? 0) > 0).sort((a, b) => {
      const aStats = this.dogUsageStats().get(a.id);
      const bStats = this.dogUsageStats().get(b.id);
      return (bStats?.races ?? 0) - (aStats?.races ?? 0)
        || (bStats?.distance ?? 0) - (aStats?.distance ?? 0)
        || a.name.localeCompare(b.name);
    });
  });
  dogUsageStats = computed(() => {
    const stats = new Map<string, DogUsageStats>();
    for (const result of this.scopedResults()) {
      const dogId = result.team?.dog?.id || result.team?.dogId;
      if (!dogId) continue;
      const current = stats.get(dogId) ?? { races: 0, distance: 0, disciplines: [] };
      const distance = result.race?.distanceKm || 0;
      current.races++;
      current.distance += distance;
      const normalized = normalizeResultDiscipline(result.discipline, result.race?.name, result.category);
      const name = normalized === 'Cani-VTT' ? 'VTT'
        : normalized === 'Cani-Trottinette' ? 'Trottinette'
        : normalized === 'Cani-Marche' ? 'Cani-marche'
        : 'Canicross';
      const discipline = current.disciplines.find(item => item.name === name);
      if (discipline) { discipline.races++; discipline.distance += distance; }
      else current.disciplines.push({ name, races: 1, distance });
      stats.set(dogId, current);
    }
    return stats;
  });
  dogStats(dogId: string): DogUsageStats {
    return this.dogUsageStats().get(dogId) ?? { races: 0, distance: 0, disciplines: [] };
  }
  isBorrowedDog(dog: Dog): boolean {
    return this.borrowedDogIds().includes(dog.id) || (!!dog.ownerId && dog.ownerId !== this.human()?.id);
  }
  async toggleBorrowedDog(dog: Dog): Promise<void> {
    const user = this.authService.currentUser();
    if (!user || this.savingDogRelationship()) return;
    this.savingDogRelationship.set(dog.id);
    this.dogRelationshipError.set(false);
    try {
      const borrowed = !this.borrowedDogIds().includes(dog.id);
      await this.dogService.setBorrowedDog(user.id, dog.id, borrowed);
      this.borrowedDogIds.update(ids => borrowed ? [...ids, dog.id] : ids.filter(id => id !== dog.id));
      this.mergeSourceDogId.set('');
      this.mergeTargetDogId.set('');
    } catch { this.dogRelationshipError.set(true); }
    finally { this.savingDogRelationship.set(null); }
  }
  humanResults = signal<HumanProfileResult[]>([]);
  savingResultDog = signal<string | null>(null);
  get translationLanguageIsFrench(): boolean { return this.translationService.currentLang() === 'fr'; }
  resultDogNotice = signal<{ resultId: string; text: string } | null>(null);

  async chooseResultDog(result: HumanProfileResult, event: Event): Promise<void> {
    const dogId = (event.target as HTMLSelectElement).value;
    if (!dogId || this.savingResultDog()) return;
    const user = this.authService.currentUser();
    const human = this.human();
    if (!user || !human) return;
    this.savingResultDog.set(result.id);
    this.resultDogNotice.set(null);
    try {
      await this.dogService.assignResultDog(result.id, dogId);
      this.profileDataService.invalidate(user.id, human.id);
      const data = await this.profileDataService.getMyProfile(user.id, true);
      this.humanResults.set(data.results);
      this.resultDogNotice.set({ resultId: result.id, text: this.translationService.currentLang() === 'fr' ? 'Chien enregistré.' : 'Dog saved.' });
    } catch {
      this.resultDogNotice.set({ resultId: result.id, text: this.translationService.currentLang() === 'fr' ? 'Impossible d’enregistrer le chien. Réessaie.' : 'Could not save the dog. Please try again.' });
    } finally {
      this.savingResultDog.set(null);
    }
  }
  
  /** Club typed in the edit form; null while the field is untouched. */
  clubSelection = signal<ClubSelection | null>(null);
  profilePhotoPreview = signal<string | null>(null);
  coverPhotoPreview = signal<string | null>(null);
  /** Photos picked in the form, uploaded to Storage when the profile is saved. */
  private pendingImages: { avatar?: File; cover?: File } = {};
  /** Which photo field received a file that is not an image. */
  photoError = signal<'avatar' | 'cover' | null>(null);
  deletedDogIds = signal<string[]>([]);
  participantCounts = signal<Record<string, number>>({});
  categoryParticipantCounts = signal<Record<string, number>>({});

  // Onboarding signals
  searchQuery = signal('');
  searchResults = signal<Human[]>([]);
  claimedSearchResults = signal<Human[]>([]);
  searchError = signal(false);
  isSearching = signal(false);
  suggestedProfile = signal<Human | null>(null);
  showManualSearch = signal(false);

  // Self-service merge signals
  showMergeSearch = signal(false);
  mergeQuery = signal('');
  mergeCandidates = signal<Human[]>([]);
  isSearchingMerge = signal(false);
  mergingHumanId = signal<string | null>(null);
  mergeNotice = signal<{ type: 'success' | 'error'; text: string } | null>(null);

  // Self-service dog merge signals
  showDogMerge = signal(false);
  mergeSourceDogId = signal<string>('');
  mergeTargetDogId = signal<string>('');
  mergingDog = signal(false);
  dogMergeNotice = signal<{ type: 'success' | 'error'; text: string } | null>(null);

  statsScope = signal<'season' | 'all'>('season');
  selectedSeasonStart = signal(currentSeasonStartYear());
  /** Set once the user picks a period; until then the latest season with results is shown. */
  private periodChosen = false;
  availableSeasons = computed(() => [...new Set([currentSeasonStartYear(), this.selectedSeasonStart(), ...this.humanResults()
    .map(result => seasonStartYear(result.event?.date)).filter((year): year is number => year !== null)])].sort((a, b) => b - a));
  statsPeriodOptions = computed<StyledSelectOption[]>(() => [
    { value: 'all', label: this.translationService.translate('humanProfile.allTime') },
    ...this.availableSeasons().map(year => ({ value: String(year), label: this.translationService.translate('humanProfile.season', this.seasonLabel(year)) })),
  ]);
  scopedResults = computed(() => this.statsScope() === 'all' ? this.humanResults()
    : this.humanResults().filter(result => seasonStartYear(result.event?.date) === this.selectedSeasonStart()));
  setStatsPeriod(value: string): void {
    this.periodChosen = true;
    if (value === 'all') this.statsScope.set('all');
    else { this.selectedSeasonStart.set(Number(value)); this.statsScope.set('season'); }
  }
  // Computed stats (same as HumanProfile)
  totalRaces = computed(() => this.scopedResults().length);
  bestRank = computed(() => {
    const ranks = this.scopedResults()
      .map(r => r.rank)
      .filter((r): r is number => r !== undefined && r !== null && r > 0);
    return ranks.length > 0 ? Math.min(...ranks) : null;
  });

  // Race history grouped by season (Sept–Aug), most recent first.
  // Unknown dates fall into their own trailing group.
  resultsBySeason = computed(() => {
    const groups = new Map<number, HumanProfileResult[]>();
    for (const result of this.scopedResults()) {
      const season = seasonStartYear(result.event?.date) ?? -1;
      const group = groups.get(season);
      if (group) group.push(result);
      else groups.set(season, [result]);
    }
    return [...groups.entries()].sort((a, b) => b[0] - a[0]);
  });

  seasonLabel(seasonStart: number): string {
    return seasonStart < 0 ? '—' : seasonLabel(seasonStart);
  }

  profileForm: FormGroup;

  get flagEmoji() {
    return this.humanService.getFlagEmoji(this.human()?.nationality);
  }

  goToPreview() {
    if (this.human()) {
      this.router.navigate(['/human-profile', this.human()!.id]);
    }
  }

  constructor() {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      dob: [''],
      age: [null, Validators.min(0)],
      gender: ['Male', Validators.required],
      club: [''],
      nationality: ['FRA'],
      imageUrl: [''],
      coverImageUrl: [''],
      dogs: this.fb.array([])
    });

    const currentUser = this.authService.currentUser();
    const cachedProfile = currentUser
      ? this.profileDataService.getCachedMyProfile(currentUser.id)
      : undefined;
    if (cachedProfile) {
      this.applyProfileData(cachedProfile);
      this.isLoading.set(false);
    }

    // Depends on the user id only: auth events for the same user must not reload the page and
    // reset a form being edited.
    const userId = computed(() => this.authService.currentUser()?.id ?? null);
    effect(() => {
      const id = userId();
      if (id) untracked(() => void this.loadMyData(id));
    });
  }

  async loadMyData(userId: string, forceRefresh = false) {
    try { this.borrowedDogIds.set(await this.dogService.getBorrowedDogIds()); }
    catch { this.dogRelationshipError.set(true); }
    if (!forceRefresh) {
      const cachedProfile = this.profileDataService.getCachedMyProfile(userId);
      if (cachedProfile) {
        this.applyProfileData(cachedProfile);
        if (!cachedProfile.human) await this.findSuggestedProfile();
        this.isLoading.set(false);
        return;
      }
    }

    this.isLoading.set(true);
    try {
      const profileData = await this.profileDataService.getMyProfile(userId, forceRefresh);
      this.applyProfileData(profileData);

      if (!profileData.human) {
        // If no linked profile, try to find a suggestion
        await this.findSuggestedProfile();
      }
    } catch (err) {
      console.error('Error loading my profile:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  private applyProfileData(profileData: Awaited<ReturnType<HumanProfileDataService['getMyProfile']>>): void {
    const human = profileData.human;
    this.human.set(human);
    this.humanResults.set(profileData.results);
    if (!this.periodChosen) {
      this.selectedSeasonStart.set(defaultSeason(profileData.results
        .map(result => seasonStartYear(result.event?.date))
        .filter((season): season is number => season !== null)));
    }
    this.participantCounts.set(profileData.participantCounts);
    this.categoryParticipantCounts.set(profileData.categoryParticipantCounts);
    this.myDogs.set(profileData.dogs);
    this.clubSelection.set(null);

    if (!human) return;

    this.profileForm.patchValue({
      firstName: this.authService.userProfile()?.firstName || human.name.split(' ')[0],
      lastName: this.authService.userProfile()?.lastName || human.name.split(' ').slice(1).join(' '),
      dob: this.authService.userProfile()?.dob || '',
      age: human.age,
      gender: human.gender,
      club: human.club,
      nationality: human.nationality,
      imageUrl: human.imageUrl,
      coverImageUrl: human.coverImageUrl,
    });
    this.pendingImages = {};
    this.photoError.set(null);
    this.setPreview(this.profilePhotoPreview, human.imageUrl || null);
    this.setPreview(this.coverPhotoPreview, human.coverImageUrl || null);

    const dogsArray = this.profileForm.get('dogs') as FormArray;
    dogsArray.clear();
    profileData.dogs.forEach(dog => {
      dogsArray.push(this.fb.group({
        id: [dog.id],
        name: [dog.name, Validators.required],
        breed: [dog.breed || ''],
        age: [dog.age, Validators.min(0)],
        birth_year: [dog.birthYear ?? null, [Validators.min(1900), Validators.max(new Date().getFullYear()), Validators.pattern(/^\d{4}$/)]],
        gender: [dog.gender, Validators.required],
        imageUrl: [dog.imageUrl],
      }));
    });
  }

  get dogsFormArray() {
    return this.profileForm.get('dogs') as FormArray;
  }

  toggleEdit() {
    this.isEditMode.set(!this.isEditMode());
  }

  onClubChange(selection: ClubSelection): void {
    this.clubSelection.set(selection);
    this.profileForm.patchValue({ club: selection.name });
  }

  addDog() {
    this.dogsFormArray.push(this.fb.group({
      id: [null],
      name: ['', Validators.required],
      breed: [''],
      age: [null, Validators.min(0)],
      birth_year: [null, [Validators.min(1900), Validators.max(new Date().getFullYear()), Validators.pattern(/^\d{4}$/)]],
      gender: ['Male', Validators.required],
      imageUrl: ['']
    }));
  }

  removeDog(index: number) {
    const dog = this.dogsFormArray.at(index).value;
    if (dog.id) {
      this.deletedDogIds.update(ids => [...ids, dog.id]);
    }
    this.dogsFormArray.removeAt(index);
  }

  async onSubmit() {
    this.profileSaveError.set(false);
    if (this.profileForm.invalid) { this.profileForm.markAllAsTouched(); return; }
    if (!this.human()) return;
    // Humans reference clubs by id: an unrecognised name cannot be saved.
    const clubSelection = this.clubSelection();
    if (clubSelection && !clubSelection.resolved) return;

    this.isLoading.set(true);
    try {
      const formData = this.profileForm.value;
      const human = this.human()!;
      const humanId = human.id;

      // 1. Upload the new photos, then update Human (Public Profile)
      const { avatar, cover } = this.pendingImages;
      const imageUrl = avatar ? await this.imageUpload.uploadHumanImage(humanId, 'avatar', avatar) : formData.imageUrl;
      const coverImageUrl = cover ? await this.imageUpload.uploadHumanImage(humanId, 'cover', cover) : formData.coverImageUrl;
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      await this.humanService.updateHuman(humanId, {
        name: fullName,
        age: formData.age,
        gender: formData.gender,
        nationality: formData.nationality,
        imageUrl,
        coverImageUrl
      });
      this.pendingImages = {};
      if (avatar && human.imageUrl !== imageUrl) void this.imageUpload.removeStoredImage(human.imageUrl);
      if (cover && human.coverImageUrl !== coverImageUrl) void this.imageUpload.removeStoredImage(human.coverImageUrl);

      // 2. Update Profile (User Data)
      const { error: profileError } = await this.authService.supabase.client
        .from('profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          dob: formData.dob || null,
          full_name: fullName
        })
        .eq('id', this.authService.currentUser()!.id);
      if (profileError) throw profileError;

      if (clubSelection && clubSelection.name !== (this.human()!.club ?? '')) {
        await this.humanService.updateHumanClub(humanId, clubSelection.id);
      }

      // 3. Handle Dogs
      const currentDogs = formData.dogs;
      for (const dogForm of currentDogs) {
        const { birth_year, ...fields } = dogForm;
        const dogData = { ...fields, birthYear: birth_year || null, age: birth_year ? new Date().getFullYear() - Number(birth_year) : fields.age };
        if (dogData.id) {
          await this.dogService.updateDog(dogData.id, dogData);
        } else {
          await this.dogService.addDog({ ...dogData, ownerId: humanId });
        }
      }

      // 3. Handle Deletions
      for (const dogId of this.deletedDogIds()) {
        await this.dogService.deleteDog(dogId);
      }
      this.deletedDogIds.set([]);

      // Refresh data
      const userId = this.authService.currentUser()!.id;
      this.profileDataService.invalidate(userId, humanId);
      await this.loadMyData(userId, true);
      this.isEditMode.set(false);
    } catch (err) {
      this.profileSaveError.set(true);
      console.error('Error saving profile:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  async findSuggestedProfile() {
    const profile = this.authService.userProfile();
    if (!profile || !profile.firstName || !profile.lastName) return;

    const fullName = `${profile.firstName} ${profile.lastName}`.trim();
    
    try {
      const tokens = nameSearchTokens(fullName);
      const anchorToken = tokens.reduce((longest, token) => token.length > longest.length ? token : longest, '');
      const humansQuery = this.authService.supabase.client
        .from('humans')
        .select('*')
        .is('profile_id', null)
        .ilike('name', `%${anchorToken}%`)
        .limit(50);
      const response = await humansQuery;
      const data = (response.data || []).filter(human => includesEveryNameToken(human.name, fullName));
      
      if (data && data.length === 1) {
        const h = data[0];
        this.suggestedProfile.set({
          id: h.id,
          name: h.name,
          age: h.age,
          gender: h.gender,
          club: h.club,
          nationality: h.nationality,
          imageUrl: h.image_url,
          coverImageUrl: h.cover_image_url
        });
      }
    } catch (err) {
      console.error('Error finding suggested profile:', err);
    }
  }

  async onSearch() {
    const q = this.searchQuery();
    if (q.length < 3) {
      this.searchResults.set([]);
      this.claimedSearchResults.set([]);
      this.searchError.set(false);
      return;
    }
    this.isSearching.set(true);
    this.searchError.set(false);
    try {
      const tokens = nameSearchTokens(q, 3);
      if (!tokens.length) {
        this.searchResults.set([]);
        this.claimedSearchResults.set([]);
        return;
      }
      const anchorToken = tokens.reduce((longest, token) => token.length > longest.length ? token : longest, '');
      const humansQuery = this.authService.supabase.client
        .from('humans')
        .select('id, name, age, gender, nationality, image_url, profile_id, club:clubs(name)')
        .ilike('name', `%${anchorToken}%`);
      const { data, error } = await humansQuery.limit(50);

      if (error) throw error;

      const mapHuman = (h: any): Human => ({
        id: h.id,
        name: h.name,
        age: h.age,
        gender: h.gender,
        club: h.club?.name ?? h.club,
        nationality: h.nationality,
        imageUrl: h.image_url,
        coverImageUrl: h.cover_image_url
      });

      const matches = (data || []).filter(h => includesEveryNameToken(h.name, q));
      this.searchResults.set(matches.filter(h => !h.profile_id).slice(0, 10).map(mapHuman));
      this.claimedSearchResults.set(matches.filter(h => !!h.profile_id).slice(0, 10).map(mapHuman));
    } catch (err) {
      console.error('Error searching profiles:', err);
      this.searchResults.set([]);
      this.claimedSearchResults.set([]);
      this.searchError.set(true);
    } finally {
      this.isSearching.set(false);
    }
  }

  async linkProfile(human: Human) {
    this.isLoading.set(true);
    try {
      const userId = this.authService.currentUser()!.id;
      await this.humanService.linkHumanToProfile(human.id, userId);
      this.profileDataService.invalidate(userId, human.id);
      await this.loadMyData(userId, true);
    } catch (err) {
      console.error('Error linking profile:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  async createNewProfile() {
    this.isLoading.set(true);
    try {
      const accountName = this.authService.userProfile()?.fullName?.trim();
      const humanName = accountName || 'Nouveau Coureur';
      const { data, error } = await this.authService.supabase.client
        .from('humans')
        .insert({
          name: humanName,
          profile_id: this.authService.currentUser()!.id,
          identity_key: accountName ? normalizePersonIdentity(accountName) : null,
          nationality: 'FRA'
        })
        .select()
        .single();
      
      if (error) throw error;
      const userId = this.authService.currentUser()!.id;
      this.profileDataService.invalidate(userId);
      await this.loadMyData(userId, true);
      this.isEditMode.set(true); // Let them complete info
    } catch (err) {
      console.error('Error creating profile:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  toggleMergeSearch() {
    this.showMergeSearch.update(value => !value);
    this.mergeNotice.set(null);
    if (!this.showMergeSearch()) {
      this.mergeQuery.set('');
      this.mergeCandidates.set([]);
    }
  }

  async searchMergeCandidates() {
    const currentHuman = this.human();
    const query = this.mergeQuery().trim();
    if (!currentHuman || query.length < 3) {
      this.mergeCandidates.set([]);
      return;
    }

    this.isSearchingMerge.set(true);
    this.mergeNotice.set(null);
    try {
      this.mergeCandidates.set(
        await this.humanService.searchUnclaimedHumans(query, currentHuman.id),
      );
    } catch {
      this.mergeCandidates.set([]);
      this.mergeNotice.set({
        type: 'error',
        text: this.translationService.translate('myProfile.merge.searchError'),
      });
    } finally {
      this.isSearchingMerge.set(false);
    }
  }

  async mergeCandidate(candidate: Human) {    const currentHuman = this.human();
    if (!currentHuman || this.mergingHumanId()) return;

    const confirmation = this.translationService.translate(
      'myProfile.merge.confirm',
      candidate.name,
      currentHuman.name,
    );
    if (!window.confirm(confirmation)) return;

    this.mergingHumanId.set(candidate.id);
    this.mergeNotice.set(null);
    try {
      await this.humanService.mergeHumanIntoMyProfile(candidate.id, currentHuman.id);
      const userId = this.authService.currentUser()!.id;
      this.profileDataService.invalidate(userId, currentHuman.id);
      await this.loadMyData(userId, true);
      this.mergeQuery.set('');
      this.mergeCandidates.set([]);
      this.showMergeSearch.set(false);
      this.mergeNotice.set({
        type: 'success',
        text: this.translationService.translate('myProfile.merge.success', candidate.name),
      });
    } catch {
      this.mergeNotice.set({
        type: 'error',
        text: this.translationService.translate('myProfile.merge.error'),
      });
    } finally {
      this.mergingHumanId.set(null);
    }
  }

  toggleDogMerge() {
    this.showDogMerge.update(value => !value);
    this.dogMergeNotice.set(null);
    if (!this.showDogMerge()) {
      this.mergeSourceDogId.set('');
      this.mergeTargetDogId.set('');
    }
  }

  canMergeDogs(): boolean {
    const sourceId = this.mergeSourceDogId();
    const targetId = this.mergeTargetDogId();
    return !!sourceId && !!targetId && sourceId !== targetId && !this.mergingDog();
  }

  async mergeDogs() {
    const currentHuman = this.human();
    const sourceId = this.mergeSourceDogId();
    const targetId = this.mergeTargetDogId();
    if (!currentHuman || !this.canMergeDogs()) return;

    const source = this.personalDogs().find(dog => dog.id === sourceId);
    const target = this.personalDogs().find(dog => dog.id === targetId);
    if (!source || !target) return;

    const confirmation = this.translationService.translate(
      'myProfile.mergeDogs.confirm',
      source.name,
      target.name,
    );
    if (!window.confirm(confirmation)) return;

    this.mergingDog.set(true);
    this.dogMergeNotice.set(null);
    try {
      await this.dogService.mergeDogs(sourceId, targetId);
      const userId = this.authService.currentUser()!.id;
      this.profileDataService.invalidate(userId, currentHuman.id);
      await this.loadMyData(userId, true);
      this.mergeSourceDogId.set('');
      this.mergeTargetDogId.set('');
      this.showDogMerge.set(false);
      this.dogMergeNotice.set({
        type: 'success',
        text: this.translationService.translate('myProfile.mergeDogs.success', source.name),
      });
    } catch {
      this.dogMergeNotice.set({
        type: 'error',
        text: this.translationService.translate('myProfile.mergeDogs.error'),
      });
    } finally {
      this.mergingDog.set(false);
    }
  }

  formatTime(totalSeconds: number): string {    if (!totalSeconds || totalSeconds <= 0) return '—';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${pad(minutes)}m ${pad(seconds)}s`;
  }

  formatDate(isoString: string): string {
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(isoString).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  onProfilePhotoSelected(event: Event) {
    this.selectImage(event, 'avatar');
  }

  onCoverPhotoSelected(event: Event) {
    this.selectImage(event, 'cover');
  }

  /** Keeps the picked photo for the next save and previews it without uploading it yet. */
  private selectImage(event: Event, kind: 'avatar' | 'cover'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.photoError.set(kind);
      return;
    }
    this.photoError.set(null);
    this.pendingImages[kind] = file;
    this.setPreview(kind === 'avatar' ? this.profilePhotoPreview : this.coverPhotoPreview, URL.createObjectURL(file));
  }

  private setPreview(preview: WritableSignal<string | null>, url: string | null): void {
    const previous = preview();
    if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous);
    preview.set(url);
  }
}
